/**
 * Centralized Font Configuration
 *
 * Change the ACTIVE_FONT constant to experiment with different font families.
 * All fonts throughout the app will update automatically.
 *
 * Works across all platforms: iOS, Android, and Web
 */

// Available font options
export const FONT_OPTIONS = {
  inter: {
    name: 'Inter',
    family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap',
  },
  roboto: {
    name: 'Roboto',
    family: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap',
  },
  poppins: {
    name: 'Poppins',
    family: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap',
  },
  openSans: {
    name: 'Open Sans',
    family: "'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;600;700&display=swap',
  },
  lato: {
    name: 'Lato',
    family: "'Lato', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap',
  },
  montserrat: {
    name: 'Montserrat',
    family: "'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap',
  },
  sourceCodePro: {
    name: 'Source Code Pro',
    family: "'Source Code Pro', 'Monaco', 'Courier New', monospace",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Source+Code+Pro:wght@400;500;600;700&display=swap',
  },
  playfairDisplay: {
    name: 'Playfair Display',
    family: "'Playfair Display', Georgia, 'Times New Roman', serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap',
  },
  plusJakartaSans: {
    name: 'Plus Jakarta Sans',
    family: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap',
  },
  outfit: {
    name: 'Outfit',
    family: "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap',
  },
  geistSans: {
    name: 'Geist Sans',
    family: "'Geist Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&display=swap',
  },
  spaceGrotesk: {
    name: 'Space Grotesk',
    family: "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap',
  },
  figtree: {
    name: 'Figtree',
    family: "'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700&display=swap',
  },
  bricolageGrotesque: {
    name: 'Bricolage Grotesque',
    family: "'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    googleFontsUrl: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@300;400;500;600;700&display=swap',
  },
  systemDefault: {
    name: 'System Default',
    family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica', 'Arial', sans-serif",
    googleFontsUrl: null, // No external font needed
  },
} as const;

export type FontOption = keyof typeof FONT_OPTIONS;

/**
 * CHANGE THIS to experiment with different fonts!
 *
 * Available options:
 * - 'inter' (current) - Clean, modern
 * - 'roboto' - Material Design standard
 * - 'poppins' - Rounded, friendly
 * - 'openSans' - Humanist, readable
 * - 'lato' - Professional, elegant
 * - 'montserrat' - Geometric, modern
 * - 'plusJakartaSans' - Modern, elegant (trending)
 * - 'outfit' - Geometric, friendly
 * - 'geistSans' - Vercel's font, clean & minimal
 * - 'spaceGrotesk' - Quirky, distinctive
 * - 'figtree' - Warm, readable
 * - 'bricolageGrotesque' - Unique, artistic
 * - 'sourceCodePro' - Monospace
 * - 'playfairDisplay' - Serif, elegant
 * - 'systemDefault' - Native system fonts
 */
export const ACTIVE_FONT: FontOption = 'figtree';

// Export the active font configuration
export const activeFontConfig = FONT_OPTIONS[ACTIVE_FONT];

/**
 * Get the font family string for use in CSS/MUI
 */
export const getFontFamily = (): string => {
  return activeFontConfig.family;
};

/**
 * Get the Google Fonts URL for the active font (if applicable)
 */
export const getFontUrl = (): string | null => {
  return activeFontConfig.googleFontsUrl;
};

/**
 * Get the display name of the active font
 */
export const getActiveFontName = (): string => {
  return activeFontConfig.name;
};
